import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import type { Piece } from './showcase';

export type Half = 'photo' | 'art' | 'full';

export function pieceSrc(piece: Piece): string {
  return `${piece.base}.webp`;
}

/**
 * One print on the wall. For a diptych, `half` picks the photo (left) or the art
 * (right) in CSS (see `.print--photo` / `.print--art`); the file itself is never
 * cropped on disk.
 */
export function Print({
  piece,
  half,
  className,
  eager,
  onMissing,
}: {
  piece: Piece;
  half: Half;
  className?: string;
  eager?: boolean;
  onMissing?: () => void;
}) {
  const [missing, setMissing] = useState(false);
  useEffect(() => setMissing(false), [piece.base]);
  const alt = half === 'photo' ? piece.photoAlt : piece.artAlt;
  const cls = ['print', piece.kind === 'diptych' && half !== 'full' ? `print--${half}` : 'print--full', className].filter(Boolean).join(' ');
  if (missing) {
    return (
      <div className={`${cls} print--missing`} role="img" aria-label={`${alt} (image unavailable)`}>
        <span>Example image unavailable</span>
      </div>
    );
  }
  return (
    <div className={cls}>
      <img
        src={pieceSrc(piece)}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={eager ? 'high' : undefined}
        draggable={false}
        onError={() => {
          setMissing(true);
          onMissing?.();
        }}
      />
    </div>
  );
}

/**
 * Before/after reveal. A real range input drives the split, so it works with
 * arrow keys, Home/End, touch and screen readers; the visible handle follows it.
 */
export function PrintCompare({ piece, intro }: { piece: Piece; intro?: boolean }) {
  // Known after mount: the prerendered page and its hydration start from the same (no-preference) state.
  const [reduced, setReduced] = useState(false);
  useEffect(() => setReduced(Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)), []);
  const reveal = Boolean(intro && !reduced);
  const [position, setPosition] = useState(reveal ? 88 : 50);
  const [animating, setAnimating] = useState(false);
  const [seen, setSeen] = useState(false);
  // Id of the piece whose divider the visitor has moved; the reveal never overrides them.
  const adjustedRef = useRef<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const labelId = useId();

  // The reveal waits until the print is actually on screen.
  useEffect(() => {
    if (!reveal || seen) return;
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setSeen(true);
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [reveal, seen]);

  // One authored moment per piece: the photo slides back to reveal the art.
  useEffect(() => {
    if (!reveal) {
      setPosition(50);
      return;
    }
    if (adjustedRef.current === piece.id) return;
    setAnimating(false);
    setPosition(88);
    if (!seen) return;
    const start = window.setTimeout(() => {
      if (adjustedRef.current === piece.id) return;
      setAnimating(true);
      setPosition(50);
    }, 240);
    const end = window.setTimeout(() => setAnimating(false), 1300);
    return () => {
      window.clearTimeout(start);
      window.clearTimeout(end);
    };
  }, [piece.id, reveal, seen]);

  if (piece.kind !== 'diptych') {
    return (
      <figure className="compare-print compare-print--single">
        <Print piece={piece} half="full" className="compare-print__frame" />
      </figure>
    );
  }

  return (
    <div
      ref={rootRef}
      className="compare-print"
      data-animating={animating}
      style={{ '--pos': `${position}%` } as CSSProperties}
    >
      <div className="compare-print__frame">
        <Print piece={piece} half="art" className="compare-print__layer" />
        <Print piece={piece} half="photo" className="compare-print__layer compare-print__photo" />
        <span className="compare-print__handle" aria-hidden="true">
          <span className="compare-print__grip" />
        </span>
        <span className="compare-print__tag compare-print__tag--photo" aria-hidden="true">
          Photo
        </span>
        <span className="compare-print__tag compare-print__tag--art" aria-hidden="true">
          Art
        </span>
        <label className="visually-hidden" htmlFor={labelId}>
          Compare the original photo with the {piece.style.toLowerCase()} art
        </label>
        <input
          id={labelId}
          className="compare-print__range"
          type="range"
          min={0}
          max={100}
          step={1}
          value={position}
          aria-valuetext={`${position}% original photo, ${100 - position}% art`}
          onChange={(event) => {
            adjustedRef.current = piece.id;
            setAnimating(false);
            setPosition(Number(event.target.value));
          }}
        />
      </div>
    </div>
  );
}
